import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AccessibilityInfo, findNodeHandle} from 'react-native';
import {useSelector} from 'react-redux';

import {usePopover} from '/provider/PopoverProvider/createPopoverProvider';
import {selectHealthCareProviderFields} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {
  showHealthcareSaveErrorToast,
  showHealthcareSaveSuccessToast,
  showLeadsPoolSaveErrorToast,
  showLeadsPoolSaveSuccessToast,
  showSupportedLanguagesSaveErrorToast,
  showSupportedLanguagesSaveSuccessToast,
} from '/util/toast.util';

import {LeadsPoolPopover} from '../LeadsPool';
import {LANGUAGES} from '../ManageMySite/ManageMySite.constants';
import {ProfileBusinessSection} from '../ProfileBusinessSection';
import {BUSINESS_ROW_KEYS} from '../ProfileBusinessSection.constants';
import {SupportedLanguagesPopover} from '../SupportedLanguages';

import type {
  HealthcareData,
  ProfileBusinessSectionContainerProps,
} from './ProfileBusinessSectionContainer.types';

const DEFAULT_LANGUAGE = 'en';

const normalizeLanguages = (languages: string[] = []): string[] => {
  const normalized = (languages?.length ? languages : [DEFAULT_LANGUAGE]).map(
    item => String(item).toLowerCase(),
  );

  const withEnglish = normalized.includes(DEFAULT_LANGUAGE)
    ? normalized
    : [...normalized, DEFAULT_LANGUAGE];

  return [...new Set(withEnglish)].sort();
};

const areStringArraysEqual = (a: string[] = [], b: string[] = []): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

const EMPTY_HEALTHCARE_PROVIDER_DATA: HealthcareData = {
  isHealthcareProvider: false,
  registeredField: '',
  registeredState: '',
  licenseNumber: '',
  otherRegisteredField: '',
};

const normalizeHealthcareData = (
  data: HealthcareData | null | undefined,
): HealthcareData | null => {
  if (data == null) {
    return null;
  }

  if (
    data.isHealthcareProvider === null ||
    data.isHealthcareProvider === undefined
  ) {
    return null;
  }

  if (data.isHealthcareProvider === false) {
    return EMPTY_HEALTHCARE_PROVIDER_DATA;
  }

  return {
    isHealthcareProvider: true,
    registeredField: String(data.registeredField ?? '').trim(),
    registeredState: String(data.registeredState ?? '').trim(),
    licenseNumber: String(data.licenseNumber ?? '').trim(),
    otherRegisteredField: String(data.otherRegisteredField ?? '').trim(),
  };
};

const areHealthcareEqual = (
  a: HealthcareData | null | undefined,
  b: HealthcareData | null | undefined,
): boolean => {
  const normalizedA = normalizeHealthcareData(a);
  const normalizedB = normalizeHealthcareData(b);

  if (normalizedA === null && normalizedB === null) {
    return true;
  }

  if (normalizedA === null || normalizedB === null) {
    return false;
  }

  return (
    normalizedA.isHealthcareProvider === normalizedB.isHealthcareProvider &&
    normalizedA.registeredField === normalizedB.registeredField &&
    normalizedA.registeredState === normalizedB.registeredState &&
    normalizedA.licenseNumber === normalizedB.licenseNumber &&
    normalizedA.otherRegisteredField === normalizedB.otherRegisteredField
  );
};

export const ProfileBusinessSectionContainerComponent = ({
  featureFlag = {},
  userId,
  onNavigate,

  leadsPoolOptIn = null,
  isLeadsPoolSaving = false,
  isLeadsPoolLoading = false,
  hasLeadsPool = false,
  getLeadsPool,
  patchLeadsPool,
  hasLeadsPoolError = false,

  supportedLanguages = [DEFAULT_LANGUAGE],
  isSupportedLanguagesSaving = false,
  isSupportedLanguagesLoading = false,
  hasSupportedLanguages = false,
  getSupportedLanguages,
  putSupportedLanguages,
  hasSupportedLanguagesError = false,

  healthcareData = null,
  hasHealthcareData = false,
  isHealthcareLoading = false,
  isHealthcareSaving = false,
  hasHealthcareError = false,
  getHealthcareData,
  putHealthcareData,
}: ProfileBusinessSectionContainerProps) => {
  const registeredFieldOptions = useSelector(selectHealthCareProviderFields);
  const languageOptions = LANGUAGES;
  const {popoverShow, popoverHide} = usePopover();

  const {
    ff_manage_my_site = '',
    ff_healthcare_provider = '',
    ff_supported_languages = '',
    ff_leads_pool_option = '',
  } = featureFlag;

  const showManageMySite = shouldReleaseFeature(ff_manage_my_site);
  const showHealthcareProvider = shouldReleaseFeature(ff_healthcare_provider);
  const showSupportedLanguages = shouldReleaseFeature(ff_supported_languages);
  const showLeadsPoolOption = shouldReleaseFeature(ff_leads_pool_option);

  const showBusinessSection =
    showManageMySite ||
    showHealthcareProvider ||
    showSupportedLanguages ||
    showLeadsPoolOption;

  const normalizedReduxHealthcareData = useMemo(
    () => normalizeHealthcareData(healthcareData),
    [healthcareData],
  );

  const normalizedReduxSupportedLanguages = useMemo(
    () => normalizeLanguages(supportedLanguages),
    [supportedLanguages],
  );

  const normalizeLeadsPoolOptIn = (
    value: boolean | null | undefined,
  ): boolean => value ?? true;

  const [localHealthcareData, setLocalHealthcareData] =
    useState<HealthcareData | null>(normalizedReduxHealthcareData);

  const [localSupportedLanguages, setLocalSupportedLanguages] = useState<
    string[]
  >(normalizedReduxSupportedLanguages);

  const [localLeadsPoolOptIn, setLocalLeadsPoolOptIn] = useState<
    boolean | null
  >(normalizeLeadsPoolOptIn(leadsPoolOptIn));

  const [healthcareSyncLocked, setHealthcareSyncLocked] = useState(false);
  const [languagesSyncLocked, setLanguagesSyncLocked] = useState(false);
  const [leadsSyncLocked, setLeadsSyncLocked] = useState(false);

  const [localHealthcareSaving, setLocalHealthcareSaving] = useState(false);
  const [localSupportedLanguagesSaving, setLocalSupportedLanguagesSaving] =
    useState(false);
  const [localLeadsPoolSaving, setLocalLeadsPoolSaving] = useState(false);

  const pendingHealthcareRef = useRef<HealthcareData | null>(null);
  const pendingLanguagesRef = useRef<string[] | null>(null);
  const pendingLeadsRef = useRef<boolean | null>(null);

  const lastTriggerRef = useRef<any>(null);

  useEffect(() => {
    if (!healthcareSyncLocked) {
      setLocalHealthcareData(prev =>
        areHealthcareEqual(prev, normalizedReduxHealthcareData)
          ? prev
          : normalizedReduxHealthcareData,
      );
      return;
    }

    if (
      areHealthcareEqual(
        normalizedReduxHealthcareData,
        pendingHealthcareRef.current,
      )
    ) {
      setLocalHealthcareData(normalizedReduxHealthcareData);
      pendingHealthcareRef.current = null;
      setHealthcareSyncLocked(false);
    }
  }, [normalizedReduxHealthcareData, healthcareSyncLocked]);

  useEffect(() => {
    if (!languagesSyncLocked) {
      setLocalSupportedLanguages(prev =>
        areStringArraysEqual(prev, normalizedReduxSupportedLanguages)
          ? prev
          : normalizedReduxSupportedLanguages,
      );
      return;
    }

    const pending = pendingLanguagesRef.current ?? [];

    if (areStringArraysEqual(normalizedReduxSupportedLanguages, pending)) {
      setLocalSupportedLanguages(normalizedReduxSupportedLanguages);
      pendingLanguagesRef.current = null;
      setLanguagesSyncLocked(false);
    }
  }, [normalizedReduxSupportedLanguages, languagesSyncLocked]);

  useEffect(() => {
    const normalizedLeadsPoolOptIn = normalizeLeadsPoolOptIn(leadsPoolOptIn);

    if (!leadsSyncLocked) {
      setLocalLeadsPoolOptIn(prev =>
        prev === normalizedLeadsPoolOptIn ? prev : normalizedLeadsPoolOptIn,
      );
      return;
    }

    if (normalizedLeadsPoolOptIn === pendingLeadsRef.current) {
      setLocalLeadsPoolOptIn(normalizedLeadsPoolOptIn);
      pendingLeadsRef.current = null;
      setLeadsSyncLocked(false);
    }
  }, [leadsPoolOptIn, leadsSyncLocked]);

  const healthcareSubtitle = useMemo(() => {
    if (hasHealthcareError && !hasHealthcareData && !localHealthcareData) {
      return 'Unable to load';
    }

    if (
      (isHealthcareLoading && !hasHealthcareData && !localHealthcareData) ||
      localHealthcareSaving ||
      isHealthcareSaving
    ) {
      return 'Loading...';
    }

    if (!localHealthcareData) {
      return 'Not registered';
    }

    if (localHealthcareData.isHealthcareProvider === false) {
      return 'Not a healthcare provider';
    }

    const {registeredField, otherRegisteredField} = localHealthcareData;

    if (!registeredField.trim()) {
      return 'Not registered';
    }

    if (registeredField === 'other') {
      return otherRegisteredField?.trim() || 'Other';
    }

    const field = registeredFieldOptions.find(
      item => item.value === registeredField,
    );

    return field?.label || registeredField || 'Not registered';
  }, [
    hasHealthcareError,
    isHealthcareLoading,
    hasHealthcareData,
    localHealthcareData,
    localHealthcareSaving,
    isHealthcareSaving,
    registeredFieldOptions,
  ]);

  const languagesSubtitle = useMemo(() => {
    if (hasSupportedLanguagesError && !hasSupportedLanguages) {
      return 'Unable to load';
    }

    if (
      (isSupportedLanguagesLoading && !hasSupportedLanguages) ||
      localSupportedLanguagesSaving ||
      isSupportedLanguagesSaving
    ) {
      return 'Loading...';
    }

    const labels =
      languageOptions
        ?.filter(lang =>
          localSupportedLanguages.includes(String(lang.value).toLowerCase()),
        )
        ?.map(lang => lang.label) || [];

    return labels.length ? labels.join(', ') : 'Not specified';
  }, [
    hasSupportedLanguagesError,
    isSupportedLanguagesLoading,
    hasSupportedLanguages,
    localSupportedLanguagesSaving,
    isSupportedLanguagesSaving,
    languageOptions,
    localSupportedLanguages,
  ]);

  const leadsSubtitle = useMemo(() => {
    if (hasLeadsPoolError && !hasLeadsPool) {
      return 'Unable to load';
    }

    if (
      (isLeadsPoolLoading && !hasLeadsPool) ||
      localLeadsPoolSaving ||
      isLeadsPoolSaving
    ) {
      return 'Loading...';
    }

    return localLeadsPoolOptIn ? 'Receiving leads' : 'Not receiving leads';
  }, [
    hasLeadsPoolError,
    isLeadsPoolLoading,
    hasLeadsPool,
    localLeadsPoolSaving,
    isLeadsPoolSaving,
    localLeadsPoolOptIn,
  ]);

  const buildHealthcarePayload = useCallback((data: HealthcareData) => {
    if (data.isHealthcareProvider !== true) {
      return {
        healthCareProvider: false,
      };
    }

    const fieldValue =
      data.registeredField === 'other'
        ? data.otherRegisteredField?.trim()
        : data.registeredField;

    return {
      healthCareProvider: true,
      field: fieldValue,
      stateProvince: data.registeredState,
      licenseNumber: data.licenseNumber.trim(),
    };
  }, []);

  const handleLeadsPoolSave = useCallback(
    async (enabled: boolean | null, onSuccess?: () => void) => {
      if (enabled === null) {
        return;
      }

      const previousValue = localLeadsPoolOptIn;

      try {
        setLocalLeadsPoolSaving(true);
        setLeadsSyncLocked(true);
        pendingLeadsRef.current = enabled;
        setLocalLeadsPoolOptIn(enabled);

        await patchLeadsPool?.({leadsPoolOptIn: enabled});

        showLeadsPoolSaveSuccessToast();
        setTimeout(() => {
          onSuccess?.();
        }, 300);

        setTimeout(() => {
          getLeadsPool?.();
        }, 5000);
      } catch (err) {
        pendingLeadsRef.current = null;
        setLeadsSyncLocked(false);
        setLocalLeadsPoolOptIn(previousValue);
        showLeadsPoolSaveErrorToast();
      } finally {
        setLocalLeadsPoolSaving(false);
      }
    },
    [localLeadsPoolOptIn, patchLeadsPool, getLeadsPool],
  );

  const handleSupportedLanguagesSave = useCallback(
    async (languages: string[], onSuccess?: () => void) => {
      const previousLanguages = localSupportedLanguages;
      const nextLanguages = normalizeLanguages(languages);

      const nextLanguagesUpper = nextLanguages.map(item =>
        String(item).toUpperCase(),
      );

      try {
        setLocalSupportedLanguagesSaving(true);
        setLanguagesSyncLocked(true);
        pendingLanguagesRef.current = nextLanguages;
        setLocalSupportedLanguages(nextLanguages);

        const languageList =
          languageOptions?.map(item => {
            const languageCodeLower = String(item.value).toLowerCase();
            const languageCodeUpper = String(item.value).toUpperCase();

            return {
              language: languageCodeUpper,
              isSupported:
                nextLanguages.includes(languageCodeLower) ||
                nextLanguagesUpper.includes(languageCodeUpper),
            };
          }) || [];

        await putSupportedLanguages?.({languageList});

        showSupportedLanguagesSaveSuccessToast();
        setTimeout(() => {
          onSuccess?.();
        }, 300);

        setTimeout(() => {
          getSupportedLanguages?.();
        }, 5000);
      } catch (err) {
        pendingLanguagesRef.current = null;
        setLanguagesSyncLocked(false);
        setLocalSupportedLanguages(previousLanguages);
        showSupportedLanguagesSaveErrorToast();
      } finally {
        setLocalSupportedLanguagesSaving(false);
      }
    },
    [
      localSupportedLanguages,
      languageOptions,
      putSupportedLanguages,
      getSupportedLanguages,
    ],
  );

  const handleHealthcareSave = useCallback(
    async (data: HealthcareData) => {
      const previousHealthcareData = localHealthcareData;

      const nextHealthcareData: HealthcareData =
        data.isHealthcareProvider === true
          ? {
              isHealthcareProvider: true,
              registeredField: data.registeredField ?? '',
              registeredState: data.registeredState ?? '',
              licenseNumber: data.licenseNumber ?? '',
              otherRegisteredField: data.otherRegisteredField ?? '',
            }
          : {
              isHealthcareProvider: false,
              registeredField: '',
              registeredState: '',
              licenseNumber: '',
              otherRegisteredField: '',
            };

      try {
        setLocalHealthcareSaving(true);
        setHealthcareSyncLocked(true);
        pendingHealthcareRef.current = nextHealthcareData;
        setLocalHealthcareData(nextHealthcareData);

        const payload = buildHealthcarePayload(nextHealthcareData);
        await putHealthcareData?.(payload);

        setTimeout(() => {
          showHealthcareSaveSuccessToast();
        }, 300);

        setTimeout(() => {
          getHealthcareData?.();
        }, 5000);
      } catch (err) {
        pendingHealthcareRef.current = null;
        setHealthcareSyncLocked(false);
        setLocalHealthcareData(previousHealthcareData);
        showHealthcareSaveErrorToast();
        throw err;
      } finally {
        setLocalHealthcareSaving(false);
      }
    },
    [
      localHealthcareData,
      buildHealthcarePayload,
      putHealthcareData,
      getHealthcareData,
    ],
  );

  const healthcareModalProps = useMemo(() => {
    const commonProps = {
      userId,
      onSave: handleHealthcareSave,
      isSaving: localHealthcareSaving || isHealthcareSaving,
    };

    if (!localHealthcareData) {
      return {
        ...commonProps,
        initialIsHealthcareProvider: null,
        initialRegisteredField: '',
        initialRegisteredState: '',
        initialLicenseNumber: '',
        initialOtherRegisteredField: '',
      };
    }

    if (localHealthcareData.isHealthcareProvider === false) {
      return {
        ...commonProps,
        initialIsHealthcareProvider: false,
        initialRegisteredField: '',
        initialRegisteredState: '',
        initialLicenseNumber: '',
        initialOtherRegisteredField: '',
      };
    }

    return {
      ...commonProps,
      initialIsHealthcareProvider: true,
      initialRegisteredField: localHealthcareData.registeredField ?? '',
      initialRegisteredState: localHealthcareData.registeredState ?? '',
      initialLicenseNumber: localHealthcareData.licenseNumber ?? '',
      initialOtherRegisteredField:
        localHealthcareData.otherRegisteredField ?? '',
    };
  }, [
    userId,
    localHealthcareData,
    localHealthcareSaving,
    isHealthcareSaving,
    handleHealthcareSave,
  ]);

  const isHealthcareDisabled = hasHealthcareError && !hasHealthcareData;
  const isSupportedLanguagesDisabled =
    hasSupportedLanguagesError && !hasSupportedLanguages;
  const isLeadsPoolDisabled = hasLeadsPoolError && !hasLeadsPool;

  const handlePopoverClose = useCallback(() => {
    popoverHide();

    setTimeout(() => {
      const node = findNodeHandle(lastTriggerRef.current?.current);

      if (node) {
        AccessibilityInfo.setAccessibilityFocus(node);
      }
    }, 800);
  }, [popoverHide]);

  const handleBusinessItemPress = useCallback(
    (key: string, payload?: {triggerRef?: React.RefObject<any>}) => {
      const triggerRef = payload?.triggerRef;

      if (triggerRef) {
        lastTriggerRef.current = triggerRef;
      }

      switch (key) {
        case BUSINESS_ROW_KEYS.MANAGE_MY_SITE:
          onNavigate?.('card', {
            modalName: BUSINESS_ROW_KEYS.MANAGE_MY_SITE,
            modalProps: {userId},
          });
          break;

        case BUSINESS_ROW_KEYS.HEALTH_CARE_PROVIDER:
          if (isHealthcareDisabled) {
            return;
          }

          onNavigate?.('card', {
            modalName: BUSINESS_ROW_KEYS.HEALTH_CARE_PROVIDER,
            modalProps: healthcareModalProps,
          });
          break;

        case BUSINESS_ROW_KEYS.SUPPORTED_LANGUAGES:
          if (isSupportedLanguagesDisabled) {
            return;
          }

          popoverShow(SupportedLanguagesPopover, {
            triggerRef,
            initialLanguages: localSupportedLanguages,
            onSave: handleSupportedLanguagesSave,
            isSaving:
              localSupportedLanguagesSaving || isSupportedLanguagesSaving,
            languageOptions,
            onClose: handlePopoverClose,
          });
          break;

        case BUSINESS_ROW_KEYS.LEADS_POOL:
          if (isLeadsPoolDisabled) {
            return;
          }

          popoverShow(LeadsPoolPopover, {
            triggerRef,
            initialReceiveLeads: localLeadsPoolOptIn ?? true,
            onSave: handleLeadsPoolSave,
            isSaving: localLeadsPoolSaving || isLeadsPoolSaving,
            onClose: handlePopoverClose,
          });
          break;

        default:
          break;
      }
    },
    [
      onNavigate,
      userId,
      healthcareModalProps,
      isHealthcareDisabled,
      isSupportedLanguagesDisabled,
      isLeadsPoolDisabled,
      localSupportedLanguages,
      handleSupportedLanguagesSave,
      localSupportedLanguagesSaving,
      isSupportedLanguagesSaving,
      languageOptions,
      localLeadsPoolOptIn,
      handleLeadsPoolSave,
      localLeadsPoolSaving,
      isLeadsPoolSaving,
      popoverShow,
      handlePopoverClose,
    ],
  );

  if (!showBusinessSection) {
    return null;
  }

  return (
    <ProfileBusinessSection
      onPress={handleBusinessItemPress}
      supportedLanguage={languagesSubtitle}
      leadsStatus={leadsSubtitle}
      healthcareStatus={healthcareSubtitle}
      showManageMySite={showManageMySite}
      showHealthcareProvider={showHealthcareProvider}
      showSupportedLanguages={showSupportedLanguages}
      showLeadsPoolOption={showLeadsPoolOption}
      disableSupportedLanguages={isSupportedLanguagesDisabled}
      disableHealthcareProvider={isHealthcareDisabled}
      disableLeadsPoolOption={isLeadsPoolDisabled}
    />
  );
};

export const ProfileBusinessSectionContainer = React.memo(
  ProfileBusinessSectionContainerComponent,
);
